import type { ReactNode } from 'react'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import { GeldFeldFuer, geldEingabe } from '@/components/geldfeld'
import {
  FeldStatus,
  FeldStatusZeichen,
  Feldgruppe,
  feldKlasse,
  useFeld,
  useStellenDaten,
  wertAn,
  type Speicherstelle,
} from '@/lib/speichern'

/**
 * AP-37 10. — Bausteine der Einstellungsseite: Beschriftung links, Feld rechts,
 * Zustand daneben (Muster der Projekt-Einstellungen, AP-36 Phase D). Eigene Kopie,
 * weil ein Feature nicht quer aus einem anderen importiert (AP-21-B).
 */

export interface FeldDef {
  feld: string
  label: string
  art: 'text' | 'zahl' | 'schalter' | 'auswahl'
  optionen?: readonly { wert: string; text: string }[]
  hinweis?: string
  /** Darf leer sein (Spalte nullable). Sonst ist ein leeres Feld ein Versehen, kein Wert. */
  leerErlaubt?: boolean
  /** Nur für `auswahl`: der gespeicherte Wert ist eine Zahl (Rundung). */
  zahlWert?: boolean
}

type Status = Parameters<typeof FeldStatus>[0]['status']

export function Zeile({ label, feld, hinweis, children }: { label: string; feld: Status; hinweis?: string; children: ReactNode }) {
  return (
    <div className="text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <label className="w-64 shrink-0 text-muted-foreground">{label}</label>
        {children}
        <FeldStatusZeichen status={feld} />
        {hinweis && <span className="text-xs text-muted-foreground">{hinweis}</span>}
      </div>
      <div className="sm:pl-[16.75rem]">
        <FeldStatus status={feld} zeichen={false} />
      </div>
    </div>
  )
}

function EinFeld({ stelle, def, darfAendern }: { stelle: Speicherstelle<unknown>; def: FeldDef; darfAendern: boolean }) {
  const f = useFeld<unknown>(stelle, def.feld, {
    art: def.art,
    label: def.label,
    gesperrt: !darfAendern,
    pflicht: def.art !== 'schalter' && !def.leerErlaubt,
    ...(def.zahlWert ? { lies: (x: string) => (x === '' ? null : Number(x)), schreib: (w: unknown) => (w == null ? '' : String(Number(w))) } : {}),
  })
  if (def.art === 'schalter')
    return (
      <Zeile label={def.label} feld={f} hinweis={def.hinweis}>
        <input type="checkbox" {...f.schalter} />
      </Zeile>
    )
  return (
    <Zeile label={def.label} feld={f} hinweis={def.hinweis}>
      {def.art === 'auswahl' ? (
        <select className={`${feldKlasse} max-w-xs`} {...f.props}>
          {(def.optionen ?? []).map((o) => (
            <option key={o.wert} value={o.wert}>
              {o.text}
            </option>
          ))}
        </select>
      ) : (
        <input className={`${feldKlasse} max-w-xs`} inputMode={def.art === 'zahl' ? 'decimal' : 'text'} {...f.props} />
      )}
    </Zeile>
  )
}

export function FelderKarte({
  titel,
  felder,
  stelle,
  darfAendern,
  gruppe,
  children,
}: {
  titel: string
  felder: readonly FeldDef[]
  stelle: Speicherstelle<unknown>
  darfAendern: boolean
  /** Die Felder sind nur zusammen gültig — speichern beim Verlassen der Karte. */
  gruppe?: boolean
  children?: ReactNode
}) {
  const werte = useStellenDaten(stelle)
  // Nur Felder, die der Server geschickt hat — der Rest gehört einem nicht
  // gebuchten Baustein und wird nicht angeboten (AP-29-N3).
  const vorhanden = felder.filter((f) => wertAn(werte, f.feld.split('.')[0]) !== undefined)
  const inhalt = vorhanden.map((f) => <EinFeld key={f.feld} stelle={stelle} def={f} darfAendern={darfAendern} />)

  return (
    <div className={formCardSm}>
      <h3 className={cardTitle}>{titel}</h3>
      {gruppe ? (
        <Feldgruppe stelle={stelle} label={titel} className="mt-3 space-y-2">
          {inhalt}
        </Feldgruppe>
      ) : (
        <div className="mt-3 space-y-2">{inhalt}</div>
      )}
      {children}
    </div>
  )
}

/** Ein Textfeld einer Listenzeile; `geld` zeigt den Betrag im Preisformat `1.234,50` (N2-2). */
export function ZellenFeld({
  stelle,
  feld,
  label,
  darf,
  klasse = '',
  pflicht,
  zahl,
  geld,
}: {
  stelle: Speicherstelle<unknown>
  feld: string
  label: string
  darf: boolean
  klasse?: string
  pflicht?: boolean
  zahl?: boolean
  geld?: boolean
}) {
  const f = useFeld<unknown>(stelle, feld, {
    label,
    pflicht,
    gesperrt: !darf,
    art: zahl || geld ? 'zahl' : 'text',
    // Decimal-Felder kommen als Text („42.5“) — erst zur Zahl, dann ins Preisformat.
    ...(geld ? { schreib: (w: unknown) => geldEingabe(w == null || w === '' ? null : Number(w)) } : {}),
  })
  return (
    <>
      <span className="flex items-center gap-1">
        {geld ? (
          <GeldFeldFuer className={`${inlineInput} ${klasse}`} aria-label={label} title={label} feld={f.props} />
        ) : (
          <input className={`${inlineInput} ${klasse}`} aria-label={label} inputMode={zahl ? 'decimal' : 'text'} {...f.props} />
        )}
        <FeldStatusZeichen status={f} />
      </span>
      <FeldStatus status={f} zeichen={false} />
    </>
  )
}

export function ZellenHaken({ stelle, feld, label, darf }: { stelle: Speicherstelle<unknown>; feld: string; label: string; darf: boolean }) {
  const f = useFeld<boolean>(stelle, feld, { art: 'schalter', label, gesperrt: !darf })
  return (
    <>
      <span className="inline-flex items-center gap-1">
        <input type="checkbox" aria-label={label} {...f.schalter} />
        <FeldStatusZeichen status={f} />
      </span>
      <FeldStatus status={f} zeichen={false} />
    </>
  )
}

export const knopfPrimaer = 'rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50'
export const tabellenKopf = 'border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground'
